import { motion, useScroll, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowDown } from "@phosphor-icons/react";
import { MaskedText } from "@/components/MaskedText";
import { scrollToId } from "@/components/Navbar";

const HERO_IMG = "https://images.unsplash.com/photo-1700678637621-07fced4fe043?q=85&w=2400&auto=format&fit=crop";

export const Hero = () => {
  const { t } = useTranslation();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 260]);
  const scale = useTransform(scrollY, [0, 900], [1.05, 1.18]);

  return (
    <section data-testid="hero-section" className="relative h-[100svh] overflow-hidden bg-ink">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Cádiz" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/20 to-ink/70" />
      </motion.div>

      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-24 md:pb-28">
        <motion.p
          data-testid="hero-kicker"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="text-sand/80 text-xs md:text-sm uppercase tracking-[0.35em] mb-6"
        >
          {t("hero.kicker")}
        </motion.p>

        <h1 data-testid="hero-title" className="leading-[0.88]">
          <MaskedText
            lines={[t("hero.line1"), t("hero.line2")]}
            lineClasses={[
              "font-serif font-medium text-sand text-[13vw] md:text-[8.5vw] lg:text-[7vw]",
              "font-sans font-light uppercase tracking-[0.4em] text-[#E3CFA9] text-[6vw] md:text-[3.2vw] lg:text-[2.6vw] mt-4",
            ]}
            delay={0.15}
          />
        </h1>

        <div className="mt-8 md:flex items-end justify-between gap-10">
          <motion.p
            data-testid="hero-sub"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="text-sand/85 text-base md:text-lg font-light max-w-md leading-relaxed"
          >
            {t("hero.sub")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="flex gap-3 mt-6 md:mt-0 shrink-0"
          >
            <button
              data-testid="hero-cta-apartments"
              onClick={() => scrollToId("#apartamentos")}
              className="px-8 py-4 bg-sand text-ink text-xs uppercase tracking-[0.2em] hover:bg-terracotta hover:text-sand transition-colors duration-300"
            >
              {t("hero.cta1")}
            </button>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-6 right-6 md:right-10 z-10 flex items-center gap-2 text-sand/60"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">{t("hero.scroll")}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>
    </section>
  );
};
